/**
 * LD50・EC50・TCID50等解析（Dose-Response Analysis）の統合関数
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';
import { probitAnalysis, probitAnalysisGLM, type ProbitResult } from './probit';
import { logisticRegression, logisticRegressionGLM, type LogisticResult } from './logistic';
import {
  reedMuenchTCID50,
  spearmanKarberTCID50,
  type TCID50Result,
  type DilutionData,
} from './tcid50';

/**
 * 計算タイプ
 */
export type AnalysisType = 'ld50' | 'ec50' | 'ec90' | 'tcid50';

/**
 * 計算方法（LD50/EC50/EC90用）
 */
export type Method = 'probit' | 'logistic' | 'reed-muench' | 'spearman-karber';

/**
 * 計算方法（TCID50用）
 */
export type TCID50Method = 'reed-muench' | 'spearman-karber';

/**
 * LD50/EC50/EC90解析の結果
 */
export interface DoseResponseResult {
  type: 'ld50' | 'ec50' | 'ec90';
  method: Method;
  value: number; // LD50/EC50/EC90値
  confidenceInterval: {
    // 95%信頼区間
    lower: number;
    upper: number;
  };
  probitResult?: ProbitResult; // プロビット解析の結果（プロビット解析の場合）
  logisticResult?: LogisticResult; // ロジスティック回帰の結果（ロジスティック回帰の場合）
}

/**
 * TCID50解析の結果
 */
export interface TCID50AnalysisResult {
  type: 'tcid50';
  method: TCID50Method;
  value: number; // TCID50値
  valueLog10: number; // TCID50値（対数スケール）
  confidenceInterval?: {
    // 95%信頼区間
    lower: number;
    upper: number;
  };
  tcid50Result: TCID50Result;
}

/**
 * LD50・EC50・TCID50等解析の統合結果
 */
export type DoseResponseAnalysisResult = DoseResponseResult | TCID50AnalysisResult;

/**
 * Reed-Muench法による連続用量データのLD50/EC50/EC90計算
 *
 * @param type 計算タイプ（ld50, ec50, ec90）
 * @param doses 用量（濃度）の配列
 * @param responses 死亡率または効果率（0-1の範囲）の配列
 * @returns 計算結果
 */
function reedMuenchDoseResponse(
  type: 'ld50' | 'ec50' | 'ec90',
  doses: number[],
  responses: number[]
): { value: number; confidenceInterval: { lower: number; upper: number } } {
  if (doses.length < 2) {
    throw new KeisukeAdapterError('minTwoPoints');
  }

  // データを用量の高い順にソート
  const sortedData = doses
    .map((dose, i) => ({ dose, response: responses[i] }))
    .sort((a, b) => b.dose - a.dose);

  const targetRate = type === 'ec90' ? 0.9 : 0.5;

  // 50%を挟む「直前・直後」の2点を見つける
  // 50%未満の点の中で、反応率が最大の点（下限）
  // 50%超の点の中で、反応率が最小の点（上限）
  let lower: { dose: number; response: number } | null = null;
  let upper: { dose: number; response: number } | null = null;

  for (const point of sortedData) {
    if (point.response < targetRate) {
      // 50%未満の点の中で、反応率が最大の点を見つける
      if (lower === null || point.response > lower.response) {
        lower = point;
      }
    } else if (point.response >= targetRate) {
      // 50%超の点の中で、反応率が最小の点を見つける
      if (upper === null || point.response < upper.response) {
        upper = point;
      }
    }
  }

  if (upper === null || lower === null) {
    const e = new Error(
      `DOSE_RESPONSE_NEED_BOTH_SIDES:${targetRate * 100}`
    ) as Error & { code: string; targetPercent: number };
    e.code = 'DOSE_RESPONSE_NEED_BOTH_SIDES';
    e.targetPercent = targetRate * 100;
    throw e;
  }

  if (Math.abs(upper.response - lower.response) < 1e-15) {
    throw new KeisukeAdapterError('reedMuenchInterpolationFailed');
  }

  // Reed-Muench法の計算
  // log₁₀(LD50) = log₁₀(D_low) + (targetRate - R_low) / (R_high - R_low) × (log₁₀(D_high) - log₁₀(D_low))
  const logUpper = Math.log10(upper.dose);
  const logLower = Math.log10(lower.dose);
  const prop = (targetRate - lower.response) / (upper.response - lower.response);
  const valueLog10 = logLower + prop * (logUpper - logLower);
  const value = Math.pow(10, valueLog10);

  // 信頼区間の計算（簡易版：用量の間隔を使用）
  const doseInterval =
    sortedData.length > 1
      ? Math.log10(sortedData[0].dose / sortedData[sortedData.length - 1].dose) /
        (sortedData.length - 1)
      : 1;
  const ciWidth = doseInterval * 0.5;

  const confidenceInterval = {
    lower: Math.pow(10, valueLog10 - ciWidth),
    upper: Math.pow(10, valueLog10 + ciWidth),
  };

  return { value, confidenceInterval };
}

/**
 * Spearman-Karber法による連続用量データのLD50/EC50/EC90計算
 *
 * 注意: Spearman-Karber法は以下の前提条件が必要です：
 * - 最低用量で反応率が0%（または実質的に0%）
 * - 最高用量で反応率が100%（または実質的に100%）
 * - 等比（constant ratio）の希釈系列
 * - 反応は単調増加
 *
 * これらの条件を満たさない場合、この方法は適用できません。
 *
 * @param type 計算タイプ（ld50, ec50, ec90）
 * @param doses 用量（濃度）の配列
 * @param responses 死亡率または効果率（0-1の範囲）の配列
 * @returns 計算結果
 */
function spearmanKarberDoseResponse(
  type: 'ld50' | 'ec50' | 'ec90',
  doses: number[],
  responses: number[]
): { value: number; confidenceInterval: { lower: number; upper: number } } {
  /** EC90 は Reed–Muench 補間（レガシー DoseResponseView と同様の扱い） */
  if (type === 'ec90') {
    return reedMuenchDoseResponse('ec90', doses, responses);
  }

  if (doses.length < 2) {
    throw new KeisukeAdapterError('minTwoPoints');
  }

  const ascending = doses
    .map((dose, i) => ({ dose, response: responses[i]! }))
    .sort((a, b) => a.dose - b.dose);

  const minR = ascending[0]!.response;
  const maxR = ascending[ascending.length - 1]!.response;
  if (minR > 0.05) {
    throw new KeisukeAdapterError('spearmanKarberMinResponse');
  }
  if (maxR < 0.95) {
    throw new KeisukeAdapterError('spearmanKarberMaxResponse');
  }

  const logD = ascending.map((p) => Math.log10(p.dose));
  const deltas: number[] = [];
  for (let i = 1; i < logD.length; i += 1) {
    deltas.push((logD[i] ?? 0) - (logD[i - 1] ?? 0));
  }
  const dMean = deltas.reduce((s, x) => s + x, 0) / deltas.length;
  const tol = 1e-6 * Math.max(1, Math.abs(dMean));
  for (const d of deltas) {
    if (Math.abs(d - dMean) > tol) {
      throw new KeisukeAdapterError('spearmanKarberEqualLogSpacing');
    }
  }

  const xHigh = logD[logD.length - 1]!;
  let sumP = 0;
  for (let i = ascending.length - 1; i >= 0; i -= 1) {
    const rate = ascending[i]!.response;
    if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
      throw new KeisukeAdapterError('responseRateOutOfRange');
    }
    sumP += rate;
  }

  const valueLog10 = xHigh + dMean / 2 - dMean * sumP;
  const value = Math.pow(10, valueLog10);
  const ciWidth = Math.abs(dMean) * 0.5;
  const confidenceInterval = {
    lower: Math.pow(10, valueLog10 - ciWidth),
    upper: Math.pow(10, valueLog10 + ciWidth),
  };
  return { value, confidenceInterval };
}

/**
 * LD50/EC50/EC90の計算
 *
 * @param type 計算タイプ（ld50, ec50, ec90）
 * @param doses 用量（濃度）の配列
 * @param responses 死亡率または効果率（0-1の範囲）の配列
 * @param method 計算方法（probit, logistic, reed-muench, spearman-karber）
 * @param dead 死亡数の配列（オプション：GLMを使用する場合）
 * @param total 総数の配列（オプション：GLMを使用する場合）
 * @returns LD50・EC50・TCID50等解析の結果
 */
export function calculateDoseResponse(
  type: 'ld50' | 'ec50' | 'ec90',
  doses: number[],
  responses: number[],
  method: Method = 'probit',
  dead?: number[],
  total?: number[]
): DoseResponseResult {
  let probitResult: ProbitResult | undefined;
  let logisticResult: LogisticResult | undefined;
  let value: number;
  let confidenceInterval: { lower: number; upper: number };

  if (method === 'probit') {
    // プロビット解析（GLM版：死亡数/総数が必須）
    if (!dead || !total || dead.length !== doses.length || total.length !== doses.length) {
      throw new KeisukeAdapterError('probitRequiresDeadTotal');
    }
    probitResult = probitAnalysisGLM(doses, dead, total);

    if (type === 'ld50' || type === 'ec50') {
      value = probitResult.ld50;
      confidenceInterval = probitResult.ld50CI;
    } else {
      value = probitResult.ec90;
      confidenceInterval = probitResult.ec90CI;
    }
  } else if (method === 'logistic') {
    // ロジスティック回帰（GLM版：死亡数/総数が必須）
    if (!dead || !total || dead.length !== doses.length || total.length !== doses.length) {
      throw new KeisukeAdapterError('logisticRequiresDeadTotal');
    }
    logisticResult = logisticRegressionGLM(doses, dead, total);

    if (type === 'ld50' || type === 'ec50') {
      value = logisticResult.ld50;
      confidenceInterval = logisticResult.ld50CI;
    } else {
      value = logisticResult.ec90;
      confidenceInterval = logisticResult.ec90CI;
    }
  } else if (method === 'reed-muench') {
    const result = reedMuenchDoseResponse(type, doses, responses);
    value = result.value;
    confidenceInterval = result.confidenceInterval;
  } else if (method === 'spearman-karber') {
    const result = spearmanKarberDoseResponse(type, doses, responses);
    value = result.value;
    confidenceInterval = result.confidenceInterval;
  } else {
    throw new KeisukeAdapterError('unknownCalculationMethod', { method });
  }

  return {
    type,
    method,
    value,
    confidenceInterval,
    probitResult,
    logisticResult,
  };
}

/**
 * TCID50の計算
 *
 * @param data 希釈倍率データの配列
 * @param method 計算方法（reed-muench, spearman-karber）
 * @returns TCID50解析の結果
 */
export function calculateTCID50(
  data: DilutionData[],
  method: TCID50Method = 'reed-muench'
): TCID50AnalysisResult {
  const tcid50Result =
    method === 'reed-muench' ? reedMuenchTCID50(data) : spearmanKarberTCID50(data);

  return {
    type: 'tcid50',
    method,
    value: tcid50Result.tcid50,
    valueLog10: tcid50Result.tcid50Log10,
    confidenceInterval: tcid50Result.confidenceInterval,
    tcid50Result,
  };
}
